// Protection-gap map across the book (owner-approved idea 38): which clients have no life cover, no income protection,
// no named beneficiaries on a life policy, no health cover, no retirement savings, no cover for the mortgage, nothing for
// the children's studies — computed only from what is recorded (a policy elsewhere is not known: «not recorded» is the
// honest wording). It feeds information / education campaigns: the wording is an invitation to check together, never
// pressure, and only clients who allowed the channel are included (the campaign audience rules).
import type { WorkspaceView } from './changeset.ts';
import { ageOn, type Day } from './dates.ts';
import { childrenOfPerson, coverageForPerson, coverageIndex, coveredCategories, familyPlanRecorded, minorChildren, type PersonCover } from './person-cover.ts';
import { policyCategory } from './policy-engine.ts';
import type { Person, Policy, PolicyBenefit } from './schema.ts';

export const GAP_AREAS = ['life_cover', 'income_protection', 'beneficiaries', 'health', 'retirement', 'mortgage', 'education'] as const;
export type GapArea = typeof GAP_AREAS[number];
/** campaign segments made from the gap map (labels: segment.<key>) */
export const GAP_SEGMENTS = ['gap_life_cover', 'gap_income_protection', 'gap_beneficiaries', 'gap_health', 'gap_retirement'] as const;
export type GapSegment = typeof GAP_SEGMENTS[number];
export type GapCell = 'ok' | 'gap' | 'na';
export interface GapRow { personId: string; cells: Record<GapArea, GapCell>; gaps: number }
export interface GapMap { rows: GapRow[]; areas: { area: GapArea; applicable: number; gaps: number; pct: number | null }[]; population: number }

const LIFE: readonly string[] = ['life', 'credit_life', 'savings', 'pension', 'education', 'group'];
const INCOME: readonly string[] = ['disability', 'critical_illness'];
const benefitIncome = (bs: readonly PolicyBenefit[]) => bs.some((b) => /income|disab|απώλεια|ανικαν|εισόδ|critical|σοβαρ|нетрудосп|доход/i.test(`${b.kind} ${b.note ?? ''}`));
const hasBeneficiary = (p: Policy) => !!(p.beneficiaries && p.beneficiaries.trim()) || (p.parties ?? []).some((x) => x.role === 'beneficiary' && (x.personId || (x.name ?? '').trim()));

/** one client: each area is ok, a gap (nothing recorded) or not applicable — cover as an insured person, in force today */
export function clientGaps(v: WorkspaceView, p: Person, today: Day, index?: Map<string, PersonCover[]>): GapRow {
  const cover = index ? index.get(p.id) ?? [] : coverageForPerson(v, p.id, today);
  const pols = cover.map((c) => c.policy);
  const cats = coveredCategories(cover);
  const has = (cs: readonly string[]) => cs.some((c) => cats.has(c as never));
  const age = p.birthDate ? ageOn(p.birthDate, today) : null;
  const working = age === null || (age >= 18 && age < 65);
  // beneficiaries: the client's own life cover (holder and insured)
  const lifePols = cover.filter((c) => c.holder).map((c) => c.policy).filter((x) => LIFE.includes(policyCategory(x)) && policyCategory(x) !== 'pension' && policyCategory(x) !== 'savings');
  const mortgage = v.where('financial_items', 'personId', p.id).some((f) => f.kind === 'liability' && /mortgage|στεγαστ|ипотек|home|σπίτι/i.test(`${f.type} ${f.label ?? ''}`) && (f.balance ?? 1) > 0);
  const kids = minorChildren(childrenOfPerson(v, p.id, today));
  const children = kids.length > 0;
  const cells: Record<GapArea, GapCell> = {
    life_cover: has(['life', 'credit_life']) ? 'ok' : 'gap',
    income_protection: !working ? 'na' : has(INCOME) || cover.some((c) => benefitIncome(c.benefits)) ? 'ok' : 'gap',
    beneficiaries: !lifePols.length ? 'na' : lifePols.every(hasBeneficiary) ? 'ok' : 'gap',
    health: has(['medical']) ? 'ok' : 'gap',
    retirement: age !== null && age >= 65 ? 'na' : has(['pension', 'savings']) ? 'ok' : 'gap',
    mortgage: !mortgage ? 'na' : has(['credit_life']) || pols.some((x) => /mortgage|στεγαστ|ипотек/i.test(x.productName)) ? 'ok' : 'gap',
    // the children's studies: a plan covering the client or a child, or one the client holds for a child
    education: !children ? 'na' : has(['education']) || familyPlanRecorded(v, p.id, today, 'education', kids) ? 'ok' : 'gap',
  };
  return { personId: p.id, cells, gaps: GAP_AREAS.filter((a) => cells[a] === 'gap').length };
}

/** the whole book: every active client (not archived), with counts per area */
export function protectionGapMap(v: WorkspaceView, today: Day): GapMap {
  const clients = v.rows('persons').filter((p) => p.stage === 'client' && !p.archivedAt);
  // one pass over the policies: each client's cover as an insured person, in force today
  const index = coverageIndex(v, today);
  const rows = clients.map((p) => clientGaps(v, p, today, index)).sort((a, b) => b.gaps - a.gaps);
  const areas = GAP_AREAS.map((area) => {
    const applicable = rows.filter((r) => r.cells[area] !== 'na').length, gaps = rows.filter((r) => r.cells[area] === 'gap').length;
    return { area, applicable, gaps, pct: applicable ? Math.round((gaps / applicable) * 100) : null };
  });
  return { rows, areas, population: clients.length };
}
export const gapAreaOfSegment = (k: GapSegment): GapArea => k.replace(/^gap_/, '') as GapArea;
